import React from 'react';
import { RefreshCw } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

export interface SensorChartStyle03Props {
  data: Array<{ time: string; temperature?: number; humidity?: number; [key: string]: any }>;
  loading?: boolean;
  title?: string;
  height?: number;
  showTemperature?: boolean;
  showHumidity?: boolean;
}

/**
 * SensorChart Style 03 - Dual Axis (Temperature & Humidity)
 * Displays temperature and humidity on separate Y-axes for better visualization
 */
export const Style03_DualAxis: React.FC<SensorChartStyle03Props> = ({
  data,
  loading = false,
  title = '過去 24 小時趨勢',
  height = 200,
  showTemperature = true,
  showHumidity = true
}) => {
  // Custom tooltip with both values
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-600 rounded-lg px-3 py-2 shadow-xl">
          <p className="text-slate-400 text-xs mb-1.5">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-slate-300 text-xs">{entry.name}:</span>
              <span className="text-white font-medium text-xs">
                {entry.value?.toFixed(1)}{entry.dataKey === 'temperature' ? '°C' : '%'}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate Y-axis domains
  // 溫度：確保至少顯示 6°C 範圍，避免視覺放大效應
  const MIN_TEMP_RANGE = 6;
  const tempValues = data.map(d => d.temperature).filter((v): v is number => v !== undefined && !isNaN(v));
  
  let tempMin: number, tempMax: number;
  if (tempValues.length > 0) {
    const dataMin = Math.min(...tempValues);
    const dataMax = Math.max(...tempValues);
    const range = dataMax - dataMin;
    
    if (range < MIN_TEMP_RANGE) {
      // 範圍太小，擴展到至少 MIN_TEMP_RANGE
      const center = (dataMin + dataMax) / 2;
      tempMin = Math.floor(center - MIN_TEMP_RANGE / 2);
      tempMax = Math.ceil(center + MIN_TEMP_RANGE / 2);
    } else {
      tempMin = Math.floor(dataMin - 1);
      tempMax = Math.ceil(dataMax + 1);
    }
  } else {
    tempMin = 18;
    tempMax = 28;
  }
  
  // 濕度：固定 0-100% 範圍
  const humidMin = 0;
  const humidMax = 100;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-300">{title}</h4>
        {loading && (
          <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
        )}
      </div>
      <div className="bg-slate-800/30 rounded-xl p-4">
        {loading ? (
          <div className="flex items-center justify-center" style={{ height }}>
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : data.length > 0 ? (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="tempGradientDual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="humidityGradientDual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis 
                dataKey="time" 
                stroke="#64748b" 
                tick={{ fill: '#94a3b8', fontSize: 10 }}
              />
              {/* Left Y-axis for Temperature */}
              {showTemperature && (
                <YAxis 
                  yAxisId="temp"
                  orientation="left"
                  stroke="#f97316"
                  tick={{ fill: '#f97316', fontSize: 10 }}
                  domain={[tempMin, tempMax]}
                  unit="°C"
                  width={40}
                  tickFormatter={(value) => value.toFixed(0)}
                />
              )}
              {/* Right Y-axis for Humidity */}
              {showHumidity && (
                <YAxis 
                  yAxisId="humidity"
                  orientation="right"
                  stroke="#3b82f6"
                  tick={{ fill: '#3b82f6', fontSize: 10 }}
                  domain={[humidMin, humidMax]}
                  unit="%"
                  width={40}
                  tickFormatter={(value) => value.toFixed(0)}
                />
              )}
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={24}
                iconSize={8}
                wrapperStyle={{ fontSize: '11px' }}
              />
              {/* Temperature Area */}
              {showTemperature && (
                <Area
                  yAxisId="temp"
                  type="monotone"
                  dataKey="temperature"
                  name="溫度"
                  stroke="#f97316"
                  fill="url(#tempGradientDual)"
                  strokeWidth={2}
                />
              )}
              {/* Humidity Area */}
              {showHumidity && (
                <Area
                  yAxisId="humidity"
                  type="monotone"
                  dataKey="humidity"
                  name="濕度"
                  stroke="#3b82f6"
                  fill="url(#humidityGradientDual)"
                  strokeWidth={2}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center text-slate-500 text-sm" style={{ height }}>
            暫無歷史數據
          </div>
        )}
      </div>
    </div>
  );
};

export default Style03_DualAxis;
